import { useCallback, useEffect, useState } from 'react';
import { PiggyBank, ShoppingBag } from 'lucide-react';
import { apiFetch } from '../../lib/apiClient';

const money = (value) => `CA$ ${Number(value || 0).toFixed(2)}`;

const PERIOD_LABELS = { day: 'today', week: 'this week', month: 'this month', year: 'this year' };

export default function AdminProfitSection({ period }) {
  const [profit, setProfit] = useState(null);
  const [sales, setSales] = useState(null);

  const load = useCallback(async () => {
    try {
      const [profitRes, salesRes] = await Promise.all([
        apiFetch(`/api/admin/profit-summary?period=${period}`),
        apiFetch(`/api/admin/sales-volume?period=${period}`),
      ]);
      if (profitRes.ok) setProfit(await profitRes.json());
      if (salesRes.ok) setSales(await salesRes.json());
    } catch (error) {
      console.error('Failed to load profit data:', error);
    }
  }, [period]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const periodLabel = PERIOD_LABELS[period] || period;
  const truckRows = (profit?.trucks || []).filter((t) => t.orders > 0);

  return (
    <div className="space-y-4" data-testid="admin-profit-section">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="bg-card rounded-xl p-6 border">
          <p className="text-sm text-muted-foreground mb-1 flex items-center gap-2">
            <PiggyBank className="w-4 h-4" />
            Profit made ({periodLabel})
          </p>
          <p
            className={`text-4xl font-bold ${Number(profit?.profit || 0) < 0 ? 'text-destructive' : 'text-primary'}`}
            data-testid="admin-profit-total"
          >
            {money(profit?.profit)}
          </p>
          <div className="text-sm text-muted-foreground mt-3 space-y-1">
            <div className="flex justify-between">
              <span>Charged (incl. tax, excl. tip)</span>
              <span>{money(profit?.charged_ex_tip)}</span>
            </div>
            <div className="flex justify-between">
              <span>
                Stripe fees ({profit?.stripe_fee_percent ?? 2.9}% + {money(profit?.stripe_fee_fixed ?? 0.3)})
              </span>
              <span>− {money(profit?.stripe_fee)}</span>
            </div>
            <div className="flex justify-between">
              <span>Transfers to vendors</span>
              <span>− {money(profit?.vendor_transfer)}</span>
            </div>
            <p className="text-xs pt-1">{profit?.orders || 0} fulfilled orders</p>
          </div>
        </div>

        <div className="bg-card rounded-xl p-6 border">
          <p className="text-sm text-muted-foreground mb-1 flex items-center gap-2">
            <ShoppingBag className="w-4 h-4" />
            Orders & products sold ({periodLabel})
          </p>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <div>
              <p className="text-3xl font-bold" data-testid="admin-orders-placed">
                {sales?.orders_placed ?? 0}
              </p>
              <p className="text-xs text-muted-foreground">Total orders placed</p>
            </div>
            <div>
              <p className="text-3xl font-bold" data-testid="admin-products-value">
                {money(sales?.products_value)}
              </p>
              <p className="text-xs text-muted-foreground">Value of products sold</p>
            </div>
          </div>
          <div className="text-sm text-muted-foreground mt-3 space-y-1">
            <div className="flex justify-between">
              <span>Paid orders (captured)</span>
              <span>{sales?.orders_captured ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Vendor base price of products</span>
              <span>{money(sales?.vendor_base_value)}</span>
            </div>
            <div className="flex justify-between">
              <span>Total money captured</span>
              <span>{money(sales?.captured_amount)}</span>
            </div>
          </div>
        </div>
      </div>

      {truckRows.length > 0 && (
        <div className="bg-card rounded-xl p-4 border overflow-x-auto">
          <h3 className="font-semibold mb-3">Profit by food truck</h3>
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3">Truck</th>
                <th className="text-right p-3">Orders</th>
                <th className="text-right p-3">Charged ex. tip</th>
                <th className="text-right p-3">Stripe fees</th>
                <th className="text-right p-3">Transfers</th>
                <th className="text-right p-3">Profit</th>
              </tr>
            </thead>
            <tbody>
              {truckRows.map((row) => (
                <tr key={row.truck_id} className="border-t">
                  <td className="p-3 font-medium">{row.truck_name}</td>
                  <td className="p-3 text-right">{row.orders}</td>
                  <td className="p-3 text-right">{money(row.charged_ex_tip)}</td>
                  <td className="p-3 text-right text-muted-foreground">{money(row.stripe_fee)}</td>
                  <td className="p-3 text-right text-muted-foreground">{money(row.vendor_transfer)}</td>
                  <td className={`p-3 text-right font-semibold ${row.profit < 0 ? 'text-destructive' : 'text-primary'}`}>
                    {money(row.profit)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
